import { useEffect, useState } from "react";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  writeBatch,
} from "firebase/firestore";

import { auth, db } from "../../lib/firebase";
import { relative } from "../../lib/format";
import { captureError } from "../../lib/errors";
import type { NotificationEntry } from "@ui/react";
import type { NotificationItem } from "../../lib/types/models";

/**
 * The web replacement for push.
 *
 * There is no VAPID key, no service worker and no web Firebase Messaging app in
 * this project, so Web Push is not on the table for v1. It is not needed: the
 * `domain-notification` function writes a mirror document to
 * `/notifications/{uid}/items` BEFORE it looks up any FCM token, so the mirror
 * is complete whether or not push is configured — and the mobile app never
 * reads it, meaning this surface costs the backend nothing new.
 *
 * Two known holes, both upstream rather than here: `job-started` and
 * `appointment-started` fall through to `default:` in the notification function
 * and write no mirror document at all. Those transitions are surfaced directly
 * on the job screens instead of being left to the bell.
 */
export const FEED_LIMIT = 50;

/** Maps the notification's `kind`/`linkId` pair to a route, mirroring the deep
 *  links `fcm_service.dart` installs on the mobile app so a tap lands in the
 *  same place on both surfaces. */
export function routeFor(item: NotificationItem): string | null {
  const kind = item.data?.["kind"];
  const linkId = item.data?.["linkId"];
  if (!linkId) return null;
  if (kind === "booking") return `/bookings/${linkId}`;
  if (kind === "job") return `/jobs/${linkId}`;
  if (kind === "conversation" || kind === "message") return `/messages/${linkId}`;
  return null;
}

/** Shapes a raw mirror document into what the shared components render. */
export function toEntry(item: NotificationItem): NotificationEntry {
  return {
    id: item.notificationId,
    title: item.title || "Update",
    body: item.body,
    read: item.read === true,
    when: relative(item.createdAt),
    href: routeFor(item),
  };
}

/**
 * The live listener behind the bell and the notifications page — a live
 * listener rather than polling: the collection is small, bounded by `max`,
 * and the rules already scope it to the owner.
 *
 * The bell always calls this with no `max`, so it gets FEED_LIMIT and shares
 * one watch target with the page's first page. The page raises `max` when
 * "Show older" is pressed and re-subscribes with the wider limit — `loading`
 * is NOT reset to true for that re-subscribe (it starts true and only that
 * initial mount should show a skeleton); a page already showing rows must not
 * flash the skeleton back over them while the wider query settles.
 */
export function useNotifications(max: number = FEED_LIMIT): {
  items: NotificationItem[];
  loading: boolean;
  failed: boolean;
} {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const uid = auth().currentUser?.uid;

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setItems([]);
      return;
    }

    setFailed(false);

    const q = query(
      collection(db(), "notifications", uid, "items"),
      orderBy("createdAt", "desc"),
      limit(max),
    );

    return onSnapshot(
      q,
      (snapshot) => {
        setItems(
          snapshot.docs.map((snap) => {
            const data = snap.data() as Record<string, unknown>;
            const createdAt = data["createdAt"];
            return {
              ...(data as unknown as NotificationItem),
              notificationId: snap.id,
              // Firestore hands back a Timestamp; everything downstream expects
              // an ISO string, as the REST payloads provide.
              createdAt:
                createdAt && typeof (createdAt as { toDate?: unknown }).toDate === "function"
                  ? (createdAt as { toDate: () => Date }).toDate().toISOString()
                  : String(createdAt ?? ""),
            };
          }),
        );
        setLoading(false);
      },
      (error) => {
        // A rules or index problem here must not take the shell down with it —
        // the bell degrades to empty and everything else keeps working.
        captureError("Notification listener failed", error);
        setLoading(false);
        setFailed(true);
      },
    );
  }, [uid, max]);

  return { items, loading, failed };
}

/** Marks the given ids read in one batch. `read` is the ONLY field the rules
 *  allow a client to change (`affectedKeys().hasOnly(['read'])`), so this
 *  write must not carry anything else along with it. */
export async function markRead(uid: string, ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const batch = writeBatch(db());
  for (const id of ids) {
    batch.update(doc(db(), "notifications", uid, "items", id), { read: true });
  }
  await batch.commit();
}

/** Marks a single notification read when it is opened, swallowing failure —
 *  read state here is cosmetic and must never block navigation. */
export function openNotification(uid: string | undefined, entry: NotificationEntry): void | Promise<void> {
  if (!uid || entry.read) return;
  return markRead(uid, [entry.id]).catch((error) => {
    captureError("Could not mark notification read", error);
  });
}
